import { useEffect } from 'react'
import type { HeadData, LinkTag, MetaTag } from '@/lib/seo'
import { buildHead } from '@/lib/seo'
import { useSite } from '@/app/site-context'

const MANAGED = 'data-managed-head'

const applyMeta = (head: HTMLHeadElement, metas: MetaTag[]): void => {
  for (const meta of metas) {
    const selector =
      meta.name === undefined
        ? `meta[property="${String(meta.property)}"]`
        : `meta[name="${meta.name}"]`
    const existing = head.querySelector<HTMLMetaElement>(selector)
    const element = existing ?? document.createElement('meta')

    if (meta.name === undefined) element.setAttribute('property', String(meta.property))
    else element.setAttribute('name', meta.name)

    element.setAttribute('content', meta.content)
    element.setAttribute(MANAGED, 'true')
    if (existing === null) head.appendChild(element)
  }
}

const applyLinks = (head: HTMLHeadElement, links: LinkTag[]): void => {
  for (const link of head.querySelectorAll(`link[${MANAGED}]`)) link.remove()

  for (const link of links) {
    const element = document.createElement('link')
    element.setAttribute('rel', link.rel)
    element.setAttribute('href', link.href)
    if (link.hrefLang !== undefined) element.setAttribute('hreflang', link.hrefLang)
    element.setAttribute(MANAGED, 'true')
    head.appendChild(element)
  }
}

const applyJsonLd = (head: HTMLHeadElement, jsonLd: string): void => {
  const existing = head.querySelector<HTMLScriptElement>('script[type="application/ld+json"]')
  const element = existing ?? document.createElement('script')
  element.setAttribute('type', 'application/ld+json')
  element.textContent = jsonLd
  if (existing === null) head.appendChild(element)
}

const applyHead = (head: HeadData): void => {
  const root = document.documentElement
  root.lang = head.lang

  document.title = head.title

  const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]')
  if (robots === null) {
    const element = document.createElement('meta')
    element.setAttribute('name', 'robots')
    element.setAttribute('content', head.robots)
    document.head.appendChild(element)
  } else {
    robots.setAttribute('content', head.robots)
  }

  applyMeta(document.head, head.metas)
  applyLinks(document.head, head.links)
  applyJsonLd(document.head, head.jsonLd)
}

export function DocumentHead() {
  const { route } = useSite()

  useEffect(() => {
    applyHead(buildHead(route))
  }, [route])

  return null
}
